import classnames from 'classnames';
import { useAppSelector } from 'hooks/useAppSelector';
import type { ClickableItem, IsDropdownItem, IsNewItem, VisibleItem } from 'layouts/main/sp-header/navigation/models';
import type { IntlKey } from 'locales';
import type { AnalyticCategoryActions } from 'models';
import { ReactNode, useCallback } from 'react';
import { DropdownItem } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { type Category, logAnalytics } from 'shared/middlewares/analytics/logger';
import type { RoutePath } from 'state/router';

type NavigationItemProps<C extends Category> = {
  dataE2eSuffix: string;
  label?: IntlKey;
  defaultMessage?: string;
  targetRoute?: RoutePath;
  category?: C;
  action?: AnalyticCategoryActions[C];
  shouldNavigate?: boolean;
  href?: string;
  target?: string;
  children?: ReactNode;
  title?: IntlKey;
} & VisibleItem &
  IsDropdownItem &
  ClickableItem &
  IsNewItem;

export function NavigationItem<C extends Category>({
  action,
  category,
  dataE2eSuffix,
  label,
  defaultMessage,
  targetRoute,
  isVisible = true,
  isDropdownItem = true,
  shouldNavigate = true,
  href,
  target,
  children,
  onClick,
  isNewItem,
  title,
}: NavigationItemProps<C>) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const currentRoute = useAppSelector(state => state.router.path);

  const onClickCallback = useCallback(() => {
    if (!!category && !!action) {
      logAnalytics.trackEvent(category, action);
    }
    if (shouldNavigate && targetRoute) {
      navigate(targetRoute);
    }
    onClick?.();
  }, [shouldNavigate, targetRoute, navigate, category, action, onClick]);

  const innerContent = (
    <>
      {label && <FormattedMessage id={label} defaultMessage={defaultMessage} />}
      {children}
    </>
  );

  const newItemBadge = isNewItem && (
    <span className="badge rounded-pill badge-info ms-2">
      <FormattedMessage id="navigationItem.new" />
    </span>
  );

  const isActive = targetRoute ? currentRoute?.includes(targetRoute) : false;

  const navItem = isDropdownItem ? (
    <DropdownItem
      href={href}
      target={target}
      active={isActive}
      data-e2e={`Route-${dataE2eSuffix}`}
      onClick={onClickCallback}
      title={title || label ? formatMessage({ id: title ?? label }) : undefined}
    >
      {innerContent}
      {newItemBadge}
    </DropdownItem>
  ) : (
    <li data-e2e={`Route-${dataE2eSuffix}`} className="navbar-item">
      <a
        href={href}
        target={target}
        className={classnames('navbar-link clickable', {
          active: isActive,
        })}
        onClick={onClickCallback}
      >
        {innerContent}
        {newItemBadge}
      </a>
    </li>
  );
  return isVisible ? navItem : undefined;
}
